// ספריית נכסים של לקוח: העלאה מרובה, חיפוש, תגיות, נקודת מוקד, גרסה חדשה, ארכיון.
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api';
import { Empty, ErrorBox, Field, Loading, Modal, fmtDate, fmtKb, useLoad, useMe } from '../ui';
import { Check, TagsInput, confirmAction, useAction } from './common';

export interface Asset {
  id: string; client_id: string; kind: string; name: string; mime: string; width: number | null; height: number | null; size_bytes: number; checksum: string;
  version: number; previous_asset_id: string | null; source: string; license_note: string; font_embed_allowed: boolean; tags: string[];
  focal_x: number; focal_y: number; status: string; url: string; created_at: string; meta?: Record<string, unknown>; deduped?: boolean;
}

export const ASSET_KINDS: Record<string, string> = { logo: 'לוגו', image: 'תמונה', background: 'רקע', icon: 'אייקון', font: 'פונט', pdf: 'PDF', other: 'אחר' };
const isImage = (a: Asset) => a.mime.startsWith('image/');

export function useClientAssets(clientId: string | null, opts: { kind?: string; campaignId?: string; status?: string } = {}) {
  const p = new URLSearchParams();
  if (clientId) p.set('clientId', clientId);
  if (opts.kind) p.set('kind', opts.kind);
  if (opts.campaignId) p.set('campaignId', opts.campaignId);
  if (opts.status) p.set('status', opts.status);
  return useLoad<Asset[]>(clientId ? `/api/assets?${p}` : null);
}

export function AssetThumb({ asset, size = 48 }: { asset: Pick<Asset, 'mime' | 'url' | 'name' | 'kind'>; size?: number }) {
  if (asset.mime.startsWith('image/')) return <img className="thumb" src={asset.url} alt={asset.name} style={{ width: size * 1.33, height: size }} loading="lazy" />;
  return <span className="thumb" style={{ width: size * 1.33, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }} aria-label={asset.name}>{ASSET_KINDS[asset.kind] ?? asset.kind}</span>;
}

export function AssetLibrary({ clientId, campaignId, onChanged }: { clientId: string; campaignId?: string; onChanged?: () => void }) {
  const me = useMe();
  const canEdit = me.role !== 'viewer';
  const [q, setQ] = useState('');
  const [qDeb, setQDeb] = useState('');
  const [kind, setKind] = useState('');
  const [tag, setTag] = useState('');
  const [status, setStatus] = useState('active');
  const [editing, setEditing] = useState<Asset | null>(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { const t = setTimeout(() => setQDeb(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const params = new URLSearchParams({ clientId, status });
  if (qDeb) params.set('q', qDeb);
  if (kind) params.set('kind', kind);
  if (tag.trim()) params.set('tag', tag.trim());
  const { data, error, loading, reload } = useLoad<Asset[]>(`/api/assets?${params}`);
  const allTags = useMemo(() => [...new Set((data ?? []).flatMap((a) => a.tags))].sort(), [data]);
  const refresh = () => { reload(); onChanged?.(); };

  return (
    <div>
      <div className="toolbar">
        <Field label="חיפוש לפי שם"><input type="search" value={q} onChange={(e) => setQ(e.target.value)} /></Field>
        <Field label="תגית">
          <input list="asset-tags" value={tag} onChange={(e) => setTag(e.target.value)} />
          <datalist id="asset-tags">{allTags.map((t) => <option key={t} value={t} />)}</datalist>
        </Field>
        <Field label="סוג">
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">הכל</option>{Object.entries(ASSET_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label="סטטוס">
          <select value={status} onChange={(e) => setStatus(e.target.value)}><option value="active">פעילים</option><option value="archived">ארכיון</option><option value="all">הכל</option></select>
        </Field>
        {canEdit && <button className="btn primary" onClick={() => setUploading(true)}>העלאת קבצים</button>}
      </div>
      <ErrorBox error={error} />
      {loading && !data && <Loading />}
      {data && data.length === 0 && <Empty>אין נכסים התואמים לסינון.</Empty>}
      {data && data.length > 0 && (
        <div className="asset-grid" aria-live="polite">
          {data.map((a) => (
            <div key={a.id} className="asset-card">
              <div className="preview">{isImage(a) ? <img src={a.url} alt={a.name} loading="lazy" /> : <span className="muted">{ASSET_KINDS[a.kind] ?? a.kind}</span>}</div>
              <div className="name" title={a.name}>{a.name}</div>
              <div className="row small" style={{ gap: 4 }}>
                <span className="badge">{ASSET_KINDS[a.kind] ?? a.kind}</span>
                {a.version > 1 && <span className="badge info">v{a.version}</span>}
                {a.status !== 'active' && <span className="badge warn">{a.status === 'archived' ? 'ארכיון' : a.status}</span>}
                {a.kind === 'font' && <span className={`badge ${a.font_embed_allowed ? 'ok' : 'err'}`}>{a.font_embed_allowed ? 'הטמעה מותרת' : 'אין רישיון הטמעה'}</span>}
              </div>
              <div className="small muted ltr">{a.width && a.height ? `${a.width}×${a.height} · ` : ''}{fmtKb(a.size_bytes)}</div>
              {a.tags.length > 0 && <div className="row" style={{ gap: 3 }}>{a.tags.map((t) => <span key={t} className="chip">{t}</span>)}</div>}
              <button className="btn small" onClick={() => setEditing(a)}>{canEdit ? 'פרטים ועריכה' : 'פרטים'}</button>
            </div>
          ))}
        </div>
      )}
      {uploading && <UploadModal clientId={clientId} campaignId={campaignId} onClose={() => setUploading(false)} onDone={refresh} />}
      {editing && <AssetModal asset={editing} canEdit={canEdit} onClose={() => setEditing(null)} onChanged={(a) => { refresh(); if (a) setEditing(a); else setEditing(null); }} />}
    </div>
  );
}

export function UploadModal({ clientId, campaignId, previousAsset, defaultKind, onClose, onDone }: { clientId: string; campaignId?: string; previousAsset?: Asset; defaultKind?: string; onClose: () => void; onDone: (assets: Asset[]) => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [kind, setKind] = useState(previousAsset?.kind ?? defaultKind ?? 'image');
  const [licenseNote, setLicenseNote] = useState(previousAsset?.license_note ?? '');
  const [fontEmbed, setFontEmbed] = useState(previousAsset?.font_embed_allowed ?? false);
  const [tags, setTags] = useState<string[]>(previousAsset?.tags ?? []);
  const [result, setResult] = useState<Asset[] | null>(null);
  const { busy, error, run } = useAction();
  const fileRef = useRef<HTMLInputElement>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!files.length) return;
    const fd = new FormData();
    fd.set('clientId', clientId); fd.set('kind', kind); fd.set('licenseNote', licenseNote); fd.set('fontEmbedAllowed', String(fontEmbed)); fd.set('tags', tags.join(','));
    if (campaignId) fd.set('campaignId', campaignId);
    if (previousAsset) fd.set('previousAssetId', previousAsset.id);
    for (const f of files) fd.append('file', f, f.name);
    const r = await run(() => api.upload<Asset[]>('/api/assets/upload', fd), previousAsset ? 'גרסה חדשה הועלתה' : 'הקבצים הועלו');
    if (r) { setResult(r); onDone(r); }
  };
  return (
    <Modal title={previousAsset ? `גרסה חדשה ל־${previousAsset.name}` : 'העלאת נכסים'} onClose={onClose}>
      {result ? (
        <div className="stack" role="status" aria-live="polite">
          <div className="alert ok">הועלו {result.length} קבצים.</div>
          {result.filter((a) => a.deduped).length > 0 && (
            <div className="alert info">
              <strong>מניעת כפילויות:</strong> הקבצים הבאים כבר קיימים בספרייה (אותו checksum) ולא נשמרו שוב — נעשה שימוש בנכס הקיים:
              <ul>{result.filter((a) => a.deduped).map((a) => <li key={a.id}>{a.name}</li>)}</ul>
            </div>
          )}
          <ul className="list-plain">{result.map((a) => <li key={a.id} className="row"><AssetThumb asset={a} size={32} /> {a.name} {a.version > 1 && <span className="badge info">v{a.version}</span>}</li>)}</ul>
          <div className="row"><button className="btn primary" onClick={onClose}>סגירה</button><button className="btn" onClick={() => { setResult(null); setFiles([]); if (fileRef.current) fileRef.current.value = ''; }}>העלאה נוספת</button></div>
        </div>
      ) : (
        <form className="stack" onSubmit={submit}>
          <Field label={previousAsset ? 'קובץ הגרסה החדשה *' : 'קבצים (אפשר כמה יחד) *'}>
            <input ref={fileRef} type="file" multiple={!previousAsset} required onChange={(e) => setFiles([...(e.target.files ?? [])])} />
          </Field>
          {files.length > 0 && <div className="small muted">{files.length} קבצים · {fmtKb(files.reduce((s, f) => s + f.size, 0))}</div>}
          <Field label="סוג">
            <select value={kind} onChange={(e) => setKind(e.target.value)} disabled={!!previousAsset}>{Object.entries(ASSET_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          </Field>
          {kind === 'logo' && <div className="alert info small">לוגו נשמר כקובץ המקור בלבד. המערכת אינה יוצרת או משחזרת לוגו באמצעות AI.</div>}
          <TagsInput value={tags} onChange={setTags} />
          <Field label="הערת רישיון / מקור"><input value={licenseNote} onChange={(e) => setLicenseNote(e.target.value)} /></Field>
          {kind === 'font' && (
            <>
              <Check checked={fontEmbed} onChange={setFontEmbed}>רישיון הפונט מתיר הטמעה בבאנרים</Check>
              <div className="alert warn small">סמנו רק אם יש רישיון הטמעה (webfont/ad embedding) בכתב. פונט ללא רישיון לא ייכלל ב-ZIP.</div>
            </>
          )}
          <ErrorBox error={error} />
          <div className="row"><button className="btn primary" disabled={busy || !files.length}>{busy ? 'מעלה…' : 'העלאה'}</button><button type="button" className="btn" onClick={onClose}>ביטול</button></div>
        </form>
      )}
    </Modal>
  );
}

function AssetModal({ asset, canEdit, onClose, onChanged }: { asset: Asset; canEdit: boolean; onClose: () => void; onChanged: (a: Asset | null) => void }) {
  const [name, setName] = useState(asset.name);
  const [tags, setTags] = useState(asset.tags);
  const [licenseNote, setLicenseNote] = useState(asset.license_note);
  const [fontEmbed, setFontEmbed] = useState(asset.font_embed_allowed);
  const [focal, setFocal] = useState({ x: asset.focal_x, y: asset.focal_y });
  const [newVersion, setNewVersion] = useState(false);
  const { busy, error, run } = useAction();
  const imgRef = useRef<HTMLImageElement>(null);
  const setFocalFromEvent = (e: React.MouseEvent) => {
    if (!canEdit || !imgRef.current) return;
    const r = imgRef.current.getBoundingClientRect();
    setFocal({ x: clamp01((e.clientX - r.left) / r.width), y: clamp01((e.clientY - r.top) / r.height) });
  };
  const onFocalKey = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 0.1 : 0.02;
    const m: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const d = m[e.key];
    if (d) { e.preventDefault(); setFocal((f) => ({ x: clamp01(f.x + d[0]), y: clamp01(f.y + d[1]) })); }
  };
  const save = async () => {
    const r = await run(() => api.patch<Asset>(`/api/assets/${asset.id}`, { name, tags, licenseNote, fontEmbedAllowed: fontEmbed, focalX: round3(focal.x), focalY: round3(focal.y) }), 'הנכס נשמר');
    if (r) onChanged({ ...asset, ...r, url: asset.url });
  };
  const archive = async () => {
    if (!confirmAction(`להעביר את "${asset.name}" לארכיון? נכס בשימוש אינו נמחק פיזית ובאנרים קיימים לא ישתנו.`)) return;
    const r = await run(() => api.del(`/api/assets/${asset.id}`), 'הנכס הועבר לארכיון');
    if (r) onChanged(null);
  };
  const restore = async () => {
    const r = await run(() => api.patch<Asset>(`/api/assets/${asset.id}`, { status: 'active' }), 'הנכס שוחזר');
    if (r) onChanged({ ...asset, ...r, url: asset.url });
  };
  if (newVersion) return <UploadModal clientId={asset.client_id} previousAsset={asset} onClose={() => setNewVersion(false)} onDone={(r) => { setNewVersion(false); onChanged(r[0] ?? null); }} />;
  return (
    <Modal title={asset.name} onClose={onClose} wide>
      <div className="grid cols-2">
        <div className="stack">
          {isImage(asset) ? (
            <>
              <div className="focal-wrap" onClick={setFocalFromEvent} onKeyDown={onFocalKey} tabIndex={canEdit ? 0 : -1} role={canEdit ? 'application' : undefined}
                aria-label={canEdit ? `נקודת מוקד: ${Math.round(focal.x * 100)}% אופקי, ${Math.round(focal.y * 100)}% אנכי. לחיצה על התמונה או חיצים לשינוי` : undefined}>
                <img ref={imgRef} src={asset.url} alt={asset.name} draggable={false} />
                <span className="focal-dot" style={{ left: `${focal.x * 100}%`, top: `${focal.y * 100}%` }} />
              </div>
              <div className="small muted">נקודת מוקד לחיתוך: <span className="ltr">{Math.round(focal.x * 100)}% , {Math.round(focal.y * 100)}%</span>{canEdit && ' — לחצו על התמונה לקביעה (או מקשי חיצים).'}</div>
            </>
          ) : <div className="empty">{ASSET_KINDS[asset.kind]} · {asset.mime}</div>}
          <a className="btn small" href={asset.url} target="_blank" rel="noopener noreferrer">פתיחת הקובץ (קישור חתום זמני)</a>
        </div>
        <div className="stack">
          <dl className="kv">
            <dt>סוג</dt><dd>{ASSET_KINDS[asset.kind]} <span className="mono">{asset.mime}</span></dd>
            <dt>מידות</dt><dd className="ltr">{asset.width && asset.height ? `${asset.width}×${asset.height}` : '—'}</dd>
            <dt>גודל</dt><dd>{fmtKb(asset.size_bytes)}</dd>
            <dt>גרסה</dt><dd>{asset.version}{asset.previous_asset_id && <span className="small muted"> (מחליפה <span className="mono">{asset.previous_asset_id}</span>)</span>}</dd>
            <dt>מקור</dt><dd>{asset.source}</dd>
            <dt>checksum</dt><dd className="mono">{asset.checksum.slice(0, 16)}…</dd>
            <dt>נוצר</dt><dd>{fmtDate(asset.created_at)}</dd>
            <dt>מזהה</dt><dd className="mono">{asset.id}</dd>
          </dl>
          <Field label="שם"><input value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} /></Field>
          <TagsInput value={tags} onChange={setTags} />
          <Field label="הערת רישיון"><input value={licenseNote} onChange={(e) => setLicenseNote(e.target.value)} disabled={!canEdit} /></Field>
          {asset.kind === 'font' && <Check checked={fontEmbed} onChange={setFontEmbed} disabled={!canEdit}>רישיון מתיר הטמעה</Check>}
          <ErrorBox error={error} />
          {canEdit && (
            <div className="row">
              <button className="btn primary" onClick={save} disabled={busy}>{busy ? 'שומר…' : 'שמירה'}</button>
              <button className="btn" onClick={() => setNewVersion(true)}>העלאת גרסה חדשה</button>
              {asset.status === 'archived' ? <button className="btn" onClick={restore} disabled={busy}>שחזור מארכיון</button> : <button className="btn danger" onClick={archive} disabled={busy}>העברה לארכיון</button>}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
const round3 = (n: number) => Math.round(n * 1000) / 1000;

/** בורר נכסים (לשיוך לקמפיין / ללוגו בערכת מותג) */
export function AssetPicker({ clientId, kind, exclude = [], onPick, onClose, title = 'בחירת נכסים', multiple = true }: { clientId: string; kind?: string; exclude?: string[]; onPick: (ids: string[]) => void; onClose: () => void; title?: string; multiple?: boolean }) {
  const { data, error, loading } = useClientAssets(clientId, { kind });
  const [sel, setSel] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const rows = (data ?? []).filter((a) => !exclude.includes(a.id) && (!q || a.name.toLowerCase().includes(q.toLowerCase()) || a.tags.some((t) => t.includes(q))));
  const toggle = (id: string) => setSel((s) => (multiple ? (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]) : [id]));
  return (
    <Modal title={title} onClose={onClose} wide>
      <div className="toolbar"><Field label="חיפוש"><input type="search" value={q} onChange={(e) => setQ(e.target.value)} /></Field></div>
      <ErrorBox error={error} />
      {loading && <Loading />}
      {data && rows.length === 0 && <Empty>אין נכסים זמינים. העלו נכסים בלשונית "חומרים" של הלקוח.</Empty>}
      <div className="asset-grid" role="group" aria-label="נכסים">
        {rows.map((a) => (
          <label key={a.id} className={`asset-card ${sel.includes(a.id) ? 'selected' : ''}`} style={{ cursor: 'pointer' }}>
            <div className="preview">{isImage(a) ? <img src={a.url} alt="" /> : <span className="muted">{ASSET_KINDS[a.kind]}</span>}</div>
            <span className="checkline"><input type={multiple ? 'checkbox' : 'radio'} name="asset-pick" checked={sel.includes(a.id)} onChange={() => toggle(a.id)} /><span className="name">{a.name}</span></span>
            <span className="small muted">{ASSET_KINDS[a.kind]}</span>
          </label>
        ))}
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        <button className="btn primary" disabled={!sel.length} onClick={() => onPick(sel)}>בחירה ({sel.length})</button>
        <button className="btn" onClick={onClose}>ביטול</button>
      </div>
    </Modal>
  );
}
